import React, { useEffect, useState } from 'react';
import './ComputingState.scss';
import { t } from 'i18next';
import { getModelNameIcon, getModelNameLabel } from '../../../helpers/ChatTypeHelpers';
import BenchmarkDonut, { BENCHMARK_SEGMENT_COUNT } from './BenchmarkDonut';

export interface ComputingModel {
  key: string; // canShowModel key (e.g. 'GPT')
  ready: boolean;
}

interface ComputingStateProps {
  title: string; // i18n key
  subtitle?: string; // i18n key (used when nothing is rotating)
  subtitleParams?: Record<string, string>; // interpolation for `subtitle`
  // Visual/behaviour:
  //  - 'models'    : cross-fading model icon in sync with a rotating "Checking {model}…" line + progress.
  //  - 'benchmark' : animated donut ("drawing the chart") + rotating competitor/benchmark messages.
  //  - default     : animated equalizer bars.
  variant?: 'models' | 'benchmark';
  // 'models' variant: per-model readiness → progress bar, "X of Y ready" and which models to cycle.
  models?: ComputingModel[];
  className?: string;
}

const ROTATE_MS = 2200;

// Rotating messages for the Share of Voice "preparing benchmark" state. Order = the animation phases:
// draw the empty chart → review competitors (loupe) → calculate & fill a segment.
const BENCHMARK_MESSAGES = ['benchmarkMsgChart', 'benchmarkMsgCompetitors', 'benchmarkMsgShares'];

// Friendly "we're still gathering data" placeholder shown in place of a skeleton while a freshly-created
// project collects its first data — so an empty widget reads as "in progress", not broken.
const ComputingState: React.FC<ComputingStateProps> = ({
  title,
  subtitle,
  subtitleParams,
  variant,
  models,
  className,
}) => {
  const total = models?.length ?? 0;
  const readyCount = models?.filter((m) => m.ready).length ?? 0;
  const pending = models?.filter((m) => !m.ready) ?? [];

  // What rotates: pending model keys ('models') or benchmark message keys ('benchmark').
  const rotation =
    variant === 'models' ? pending.map((m) => m.key) : variant === 'benchmark' ? BENCHMARK_MESSAGES : [];

  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (rotation.length === 0) return;
    const id = setInterval(() => setTick((s) => s + 1), ROTATE_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotation.length, variant]);

  const current = rotation.length > 0 ? rotation[tick % rotation.length] : null;
  const modelIcon = variant === 'models' && current ? getModelNameIcon(current) : null;

  // Benchmark animation phase (which message/visual) and how many donut segments are filled. `filled`
  // loops 0→all→0 (one more per message-cycle) so the chart keeps building up instead of staying full.
  const benchPhase = variant === 'benchmark' ? tick % BENCHMARK_MESSAGES.length : 0;
  const benchFilled =
    variant === 'benchmark'
      ? Math.floor((tick + 1) / BENCHMARK_MESSAGES.length) % (BENCHMARK_SEGMENT_COUNT + 1)
      : 0;
  const showProgress = variant === 'models' && total > 0;
  const progressPct = total > 0 ? Math.round((readyCount / total) * 100) : 0;

  const rotatingText =
    variant === 'models' && current
      ? t('checkingModel', { model: getModelNameLabel(current) })
      : variant === 'benchmark' && current
      ? t(current)
      : subtitle
      ? t(subtitle, subtitleParams)
      : null;

  return (
    <div className={`computing-state${className ? ` ${className}` : ''}`}>
      {variant === 'models' && modelIcon ? (
        <span className="computing-state__glow" aria-hidden="true">
          <span className={`${modelIcon} computing-state__icon`} key={current} />
        </span>
      ) : variant === 'benchmark' ? (
        <BenchmarkDonut phase={benchPhase} filled={benchFilled} />
      ) : (
        <span className="computing-state__bars" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
          <i />
        </span>
      )}

      <div className="computing-state__text">
        <p className="computing-state__title">{t(title)}</p>
        {rotatingText && (
          <p className="computing-state__subtitle" key={current ?? 'static'}>
            {rotatingText}
          </p>
        )}
      </div>

      {showProgress && (
        <div className="computing-state__progress">
          <div className="computing-state__bar">
            <span className="computing-state__bar-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="computing-state__count">
            {t('modelsReadyCount', { ready: readyCount, total })}
          </span>
        </div>
      )}
    </div>
  );
};

export default ComputingState;
